import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    AlertTriangle,
    ArrowDown,
    ArrowUp,
    CalendarClock,
    CheckCircle2,
    Clock,
    Eye,
    EyeOff,
    FileText,
    Film,
    Info,
    KeyRound,
    Landmark,
    Paperclip,
    Plus,
    RotateCcw,
    Stamp,
    Trash2,
    UploadCloud,
    Users,
    Wallet,
    X
} from 'lucide-react';

import {
    ActionButton,
    Avatar,
    Field,
    Modal,
    Notice,
    SelectInput,
    StatusPill,
    TextInput,
    cx
} from '../../customer/account/account.ui';
import { formatMoney, titleCaseName } from '../../customer/account/filing/filing.helpers';
import AccessSetupModal from '../../customer/account/filing/access.setup.modal';
import { failureCopyOf } from '../../customer/account/filing/codes.helpers';
import { filingVerbs } from './filing.labels';
import {
    NOT_INFORMED,
    DEADLINE_TO_CONFIRM,
    caseSummary,
    deadlineState,
    fontNoticeNames,
    formatDay,
    mergeAttachments,
    syncAlternatives,
    authorizedMissingOf,
    moveItem,
    pendenciesOf,
    secretReasonOf,
    secretSuggestion,
    sendableDocuments,
    signersWithEnablement,
    totalPages
} from './filing.rules';

import filingService from '../../../domain/services/filing/filing.service';
import documentsService from '../../../domain/services/solicitation/documents.service';

const FINAL_ACCEPT = '.pdf,.doc,.docx';
// Word também: o robô converte para PDF ao protocolar, como faz com a nossa memória de cálculo (.docx).
const ATTACH_ACCEPT = '.pdf,.doc,.docx,.jpg,.jpeg,.png';

// Motivos que o próprio cliente resolve aqui, conectando o acesso do tribunal.
const CONNECTABLE = ['TRIBUNAL_NAO_CONECTADO', 'TRIBUNAL_RECONECTAR', 'CODIGO_ATIVACAO_FALHOU', 'CODIGO_EM_ATIVACAO'];

const SIGNER_REASONS = {
    CERTIFICADO_VENCIDO: 'Certificado vencido. Envie o novo em Minha conta.',
    TRIBUNAL_NAO_CONECTADO: 'Falta conectar este tribunal em Minha conta.',
    TRIBUNAL_RECONECTAR: 'O tribunal pediu o código de novo. Reconecte em Minha conta.'
};

const GROUP_TITLES = { fees: 'Custas' };
const ALTERNATIVE_LABELS = { paid: 'Guia paga', free: 'Justiça gratuita', after_distribution: 'Custas após a distribuição' };

const DOCUMENT_TYPE_LABELS = {
    power_of_attorney: 'Procuração',
    residence_proof: 'Comprovante de residência',
    fee_slip: 'Guia de custas',
    fee_receipt: 'Comprovante de pagamento',
    free_justice: 'Justiça gratuita',
    mandatory_copy: 'Cópia obrigatória',
    personal_document: 'Documento pessoal',
    main_piece: 'Peça principal',
    other: 'Anexo'
};

const FAMILY_DOCUMENT_TYPES = ['power_of_attorney', 'personal_document', 'residence_proof'];

const documentTypeLabel = (document) =>
    document?.typeLabel ||
    DOCUMENT_TYPE_LABELS[document?.documentType] ||
    (document?.documentType && !/^[a-z0-9_]+$/i.test(document.documentType)
        ? document.documentType
        : 'Anexo');

// F186: no Meu INSS quem entra é o segurado (CPF + senha gov.br); não há certificado nem quem assina.
const INSS_AUTO = 'auto';
const INSS_SERVICES = [
    { value: INSS_AUTO, name: 'Automático (pela petição)' },
    { value: 'Aposentadoria por Idade Urbana', name: 'Aposentadoria por Idade Urbana' },
    { value: 'Aposentadoria por Tempo de Contribuição', name: 'Aposentadoria por Tempo de Contribuição' },
    { value: 'Aposentadoria por Idade Rural', name: 'Aposentadoria por Idade Rural' }
];

const isMeuInss = (court) => court?.system === 'meu_inss' || court?.requiresCredentials === true;

const cpfDigits = (value) => String(value || '').replace(/\D/g, '').slice(0, 11);

const formatCpf = (value) =>
    cpfDigits(value)
        .replace(/^(\d{3})(\d)/, '$1.$2')
        .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1-$2');

// Dígitos verificadores do CPF; 11 números iguais não valem.
const isValidCpf = (value) => {
    const digits = cpfDigits(value);
    if (digits.length !== 11 || /^(\d)\1{10}$/.test(digits)) return false;
    const check = (size) => {
        const sum = digits
            .slice(0, size)
            .split('')
            .reduce((total, digit, index) => total + Number(digit) * (size + 1 - index), 0);
        return ((sum * 10) % 11) % 10;
    };
    return check(9) === Number(digits[9]) && check(10) === Number(digits[10]);
};

const CPF_INVALID = 'CPF inválido. Confira os 11 números do segurado.';
const PROCURADOR_MISSING =
    'Para protocolar no Meu INSS, complete o CPF do advogado em Minha conta. Nada foi cobrado.';
const INSS_SERVICE_INVALID =
    'Não conseguimos usar esse serviço do INSS. Escolha "Automático (pela petição)" e tente de novo. Nada foi cobrado.';

// Erros que significam que o painel aberto ficou velho.
const STALE_PANEL_ERRORS = [
    'DOCUMENTOS_OBRIGATORIOS',
    'ASSINANTE_INDISPONIVEL',
    'TERMO_NAO_ACEITO',
    'TERMO_PRECO_NAO_ACEITO',
    'PROTOCOLO_INDISPONIVEL',
    'TRIBUNAL_OBRIGATORIO',
    'deadlinePassed',
    'deadlineTooClose',
    'lawyerNotEnabled',
    'DOCUMENTO_INTERNO',
    'LISTA_DE_ANEXOS_INVALIDA',
    'ASSINANTES_INVALIDOS',
    'DOCUMENTO_INVALIDO',
    'CERTIFICADO_INDISPONIVEL',
    'PROCURADOR_SEM_CPF'
];

// Saldo: 402 SALDO_INSUFICIENTE, com { fee, remaining }.
const BALANCE_ERRORS = ['SALDO_INSUFICIENTE'];

// Recusas do pedido com o texto que o cliente entende. Nada é cobrado em nenhuma delas.
const REFUSALS = {
    deadlinePassed:
        'O prazo deste processo já venceu. Não fazemos o pedido fora do prazo. Nada foi cobrado.',
    deadlineTooClose:
        'Aceitamos o pedido até as 23h59 da véspera do prazo. Para não perder o prazo, protocole direto no tribunal. Nada foi cobrado.',
    lawyerNotEnabled:
        'Quem assina não aparece como advogado habilitado neste processo. Escolha outro advogado. Nada foi cobrado.',
    JA_PROTOCOLADO:
        'Esta solicitação já está marcada como protocolada (campo "Serviço protocolado em"). Para não protocolar duas vezes, não fizemos o pedido. Nada foi cobrado.'
};

// Sem resposta não dá para saber se o pedido chegou: a tela recarrega e mostra o que valeu.
const NETWORK_ERROR =
    'Não tivemos resposta da Facilita. Confira a internet: atualizamos esta página com a situação do pedido.';

const sameFile = (a, b) =>
    !!a && !!b && a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

// A API manda o motivo em texto; o código só é traduzido se vier cru.
const signerReasonText = (signer) => {
    if (signer?.reasonCode === 'CODIGO_ATIVACAO_FALHOU') {
        const failure = failureCopyOf({ autoActivation: true, failure: signer.failure });
        return `${failure.title}. ${failure.action}`;
    }
    if (signer?.reasonCode === 'CODIGO_EM_ATIVACAO')
        return 'Estamos ativando o código. Aguarde ficar pronto para pedir o protocolo.';
    const reason = signer?.reason || '';
    if (/^[A-Z_]+$/.test(reason))
        return SIGNER_REASONS[reason] || 'Certificado indisponível para este tribunal.';

    return reason || 'Nenhum advogado está conectado a esse tribunal.';
};

// origin 'filing': anexo para o tribunal, não insumo da peça. A API não pausa a entrega nem avisa a equipe.
const uploadOne = async (requestId, file, extra = {}) => {
    const { additionalInformation, ...rest } = extra;
    const [uploaded] =
        (await documentsService.saveAll(
            requestId,
            [
                Object.assign(file, {
                    isVisibleCustomer: true,
                    additionalInformation: { origin: 'filing', ...additionalInformation },
                    ...rest
                })
            ],
            false
        )) || [];
    if (!uploaded?.success || !uploaded?.document?.id) throw new Error('UPLOAD_FAILED');

    return uploaded.document;
};

function Block({ title, aside, children }) {
    return (
        <section className="flex flex-col gap-2">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="m-0 text-xs font-semibold uppercase tracking-wide text-[#8a8a94]">
                    {title}
                </h3>
                {aside}
            </div>
            {children}
        </section>
    );
}

// Um documento pedido: anexado (com os nomes) ou com o motivo e o botão de anexar. covered = outro arquivo da
// mesma opção já atende (ex.: guia paga com a guia OU o comprovante), então a linha não pede mais atenção.
// F268: o mesmo texto que a API grava com o pedido (filing.client.list.js).
const LIST_ACKNOWLEDGEMENT_TEXT = 'Conferi a lista. Os documentos que tirei, dispensei ou deixei sem arquivo não vão ao tribunal, e essa escolha é minha.';

function RequirementRow({ item, requestId, uploading, onPick, availableDocuments = [], onSelect, covered = false, waived = false, onWaive }) {
    const done = item.noFile || item.documents?.length > 0;
    const pending = item.required && !covered && !waived;
    const selectableDocuments = availableDocuments.filter((document) =>
        document.requestId == null || Number(document.requestId) === Number(requestId) ||
        FAMILY_DOCUMENT_TYPES.includes(item.documentType)
    );

    return (
        <div
            className={cx(
                'flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3',
                done
                    ? 'border-[#ececf0] bg-white'
                    : pending
                    ? 'border-[#f3d9b1] bg-[#fdf6ec]'
                    : 'border-[#ececf0] bg-[#fafafb]'
            )}
        >
            {done ? (
                <CheckCircle2 className="h-4 w-4 shrink-0 text-[#1f7a4f]" aria-hidden="true" />
            ) : (
                <Paperclip
                    className={cx(
                        'h-4 w-4 shrink-0',
                        pending ? 'text-[#9a5512]' : 'text-[#8a8a94]'
                    )}
                    aria-hidden="true"
                />
            )}
            <span className="min-w-0 flex-1 basis-[calc(100%-2rem)] text-sm sm:basis-0">
                <span className="font-medium">{item.label}</span>
                {!item.required && <span className="text-[#8a8a94]"> · opcional</span>}
                <span
                    className="block break-words text-xs text-[#6b6b76]"
                    title={
                        !item.noFile && done
                            ? item.documents.map((document) => document.name).join(', ')
                            : undefined
                    }
                >
                    {!item.noFile && done
                        ? item.documents.map((document) => document.name).join(', ')
                        : item.reason}
                </span>
                {/* F268: prova citada na peça não trava o pedido; o cliente segue sem ela sabendo o efeito. */}
                {item.cited && !done && (
                    <span className="block text-xs text-[#9a5512]">
                        A peça cita este documento. Se não tiver, siga sem ele: o juiz pode pedir que seja juntado depois.
                    </span>
                )}
                {/* F268: exigido que o cliente decidiu não mandar (procuração, custas e cópias do agravo não têm a opção). */}
                {waived && !done && (
                    <span className="block text-xs text-[#9a5512]">
                        Você vai seguir sem este documento. O tribunal pode pedir depois, e essa escolha é sua.
                    </span>
                )}
            </span>
            {item.waivable && !done && onWaive && (
                <ActionButton
                    size="sm"
                    variant="secondary"
                    aria-label={`${waived ? 'Desfazer' : 'Seguir sem'}: ${item.label}`}
                    onClick={() => onWaive(item)}
                >
                    {waived ? 'Desfazer' : 'Seguir sem'}
                </ActionButton>
            )}
            {!item.noFile && selectableDocuments.length > 0 && (
                <select
                    aria-label={`Selecionar anexo: ${item.label}`}
                    className="w-full min-w-0 rounded-lg border border-[#dcdce2] bg-white p-2 text-xs sm:w-auto sm:max-w-[220px]"
                    value={item.multiple ? '' : item.documents?.[0]?.id || ''}
                    disabled={!!uploading}
                    onChange={(event) => onSelect(item, Number(event.target.value))}
                >
                    <option value="">{item.multiple ? 'Adicionar anexo existente' : 'Selecionar anexo existente'}</option>
                    {selectableDocuments.map((document) => (
                        <option key={document.id} value={document.id}>{document.name}</option>
                    ))}
                </select>
            )}
            {!item.noFile && (
                <ActionButton
                    size="sm"
                    variant="secondary"
                    icon={UploadCloud}
                    loading={uploading === item.key}
                    disabled={!!uploading && uploading !== item.key}
                    aria-label={`${!done ? 'Anexar' : item.multiple ? 'Anexar mais' : 'Trocar'}: ${
                        item.label
                    }`}
                    onClick={() => onPick(item)}
                >
                    {!done ? 'Anexar' : item.multiple ? 'Anexar mais' : 'Trocar'}
                </ActionButton>
            )}
        </div>
    );
}

function Choice({ name, checked, disabled, onSelect, children }) {
    return (
        <label
            className={cx(
                'flex cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 transition',
                checked
                    ? 'border-primary bg-primaryTint'
                    : 'border-[#ececf0] bg-white hover:border-[#c4c4cc]',
                disabled && 'cursor-default bg-[#fafafb] hover:border-[#ececf0]'
            )}
        >
            <input
                type="radio"
                name={name}
                className="form-radio h-4 w-4 border-[#c4c4cc] text-primary focus:ring-primary"
                checked={checked}
                disabled={disabled}
                onChange={onSelect}
            />
            {children}
        </label>
    );
}

function InfoLine({ label, children }) {
    return (
        <div className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:gap-2">
            <dt className="shrink-0 text-xs text-[#8a8a94] sm:w-24">{label}</dt>
            <dd
                className={cx(
                    'm-0 min-w-0 break-words text-[13px]',
                    children === NOT_INFORMED ? 'text-[#8a8a94]' : 'text-[#1d1d1f]'
                )}
            >
                {children}
            </dd>
        </div>
    );
}

// Prazo no topo: vence hoje ou amanhã em destaque, vencido trava. Sem prazo lido dos autos, "a confirmar" (termo 6.2).
function DeadlineLine({ state, source }) {
    return (
        <div className="flex flex-wrap items-center gap-2">
            <CalendarClock
                className={cx(
                    'h-4 w-4',
                    state.overdue
                        ? 'text-[#b3261e]'
                        : state.dueToday || state.dueTomorrow
                        ? 'text-[#9a5512]'
                        : 'text-[#8a8a94]'
                )}
                aria-hidden="true"
            />
            <span className="text-[13px] text-[#6b6b76]">Prazo final</span>
            <span
                className={cx(
                    'text-sm font-semibold',
                    state.known ? 'text-[#1d1d1f]' : 'font-normal text-[#8a8a94]'
                )}
            >
                {state.known ? formatDay(state.day) : DEADLINE_TO_CONFIRM}
            </span>
            {state.overdue ? (
                <StatusPill tone="danger">Vencido</StatusPill>
            ) : state.dueToday ? (
                <StatusPill tone="warning">Vence hoje</StatusPill>
            ) : state.dueTomorrow ? (
                <StatusPill tone="warning">Vence amanhã</StatusPill>
            ) : null}
            {state.known && source && (
                <span className="w-full text-xs text-[#8a8a94] sm:w-auto">· {source}</span>
            )}
            <span className="w-full text-xs text-[#8a8a94]">
                {state.known
                    ? 'Prazo lido nos autos, como referência: o controle do prazo continua sendo seu.'
                    : 'Ainda não lemos o prazo nos autos: o controle do prazo é seu.'}
            </span>
        </div>
    );
}

function SignerRow({
    signer,
    position,
    selected,
    isPrincipal,
    ordered,
    courtPending,
    canConnect,
    connectLabel,
    connecting,
    onToggle,
    onPrincipal,
    onConnect
}) {
    const disabled = !signer.ready;
    const notEnabled = signer.habilitado === false;
    const name = titleCaseName(signer.name);

    return (
        <div
            className={cx(
                'flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3 transition sm:flex-nowrap',
                selected
                    ? notEnabled
                        ? 'border-[#e9a34a] bg-[#fef8f0]'
                        : 'border-primary bg-primaryTint'
                    : 'border-[#ececf0] bg-white',
                disabled && 'bg-[#fafafb]'
            )}
        >
            <label
                className={cx(
                    'flex min-w-0 flex-1 items-center gap-3',
                    disabled ? 'cursor-default' : 'cursor-pointer'
                )}
            >
                <input
                    type="checkbox"
                    className="form-checkbox h-4 w-4 rounded border-[#c4c4cc] text-primary focus:ring-primary"
                    checked={selected}
                    disabled={disabled}
                    onChange={onToggle}
                />
                <Avatar name={name} size="sm" />
                <span className="min-w-0 flex-1">
                    <span
                        className={cx(
                            'flex flex-wrap items-center gap-1.5 text-sm font-medium',
                            disabled && 'text-[#8a8a94]'
                        )}
                    >
                        <span className="truncate">{name}</span>
                        {isPrincipal && <StatusPill tone="brand">Principal</StatusPill>}
                        {ordered && !isPrincipal && <StatusPill>{`${position + 1}º`}</StatusPill>}
                    </span>
                    <span className="block text-xs text-[#6b6b76]">
                        {signer.ready
                            ? [
                                  courtPending ? 'Certificado pronto' : 'Conectado a este tribunal',
                                  signer.oab && `OAB ${signer.oab}`
                              ]
                                  .filter(Boolean)
                                  .join(' · ')
                            : signerReasonText(signer)}
                    </span>
                    {signer.ready && notEnabled && (
                        <span className="block text-xs font-medium text-[#9a5512]">
                            Não aparece como advogado habilitado neste processo.
                        </span>
                    )}
                </span>
            </label>
            {ordered && !isPrincipal && (
                <ActionButton size="sm" variant="ghost" onClick={onPrincipal}>
                    Tornar principal
                </ActionButton>
            )}
            {canConnect ? (
                <ActionButton
                    size="sm"
                    variant="secondary"
                    icon={KeyRound}
                    loading={connecting}
                    onClick={onConnect}
                >
                    {connectLabel}
                </ActionButton>
            ) : (
                disabled && (
                    <Link
                        to="/profile#filing"
                        className="shrink-0 text-xs font-medium text-primary hover:underline"
                    >
                        Resolver
                    </Link>
                )
            )}
        </div>
    );
}

function AttachmentRow({ index, count, document, item, secretReason, onMove, onRemove, onSecret }) {
    const label = document.name || `Documento ${document.id}`;

    return (
        <li className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-[#ececf0] bg-white px-3 py-2.5 sm:flex-nowrap">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#f2f2f5] text-xs font-semibold text-[#55555f]">
                {index + 2}
            </span>
            <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium" title={label}>
                    {label}
                </span>
                <span className="block text-xs text-[#6b6b76]">
                    {[
                        documentTypeLabel(document),
                        Number(document.pages) > 0 ? `${document.pages} pág.` : null
                    ]
                        .filter(Boolean)
                        .join(' · ')}
                    {document.url && (
                        <>
                            {' '}
                            ·{' '}
                            <a
                                href={document.url}
                                target="_blank"
                                rel="noreferrer"
                                className="font-medium text-primary hover:underline"
                            >
                                Ver
                            </a>
                        </>
                    )}
                </span>
                {secretReason && item.secret && (
                    <span className="block text-xs text-[#6b6b76]">
                        Sugerimos sigilo: {secretReason}
                    </span>
                )}
            </span>
            <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-xs text-[#3a3a44]">
                <input
                    type="checkbox"
                    className="form-checkbox h-3.5 w-3.5 rounded border-[#c4c4cc] text-primary focus:ring-primary"
                    checked={!!item.secret}
                    onChange={(event) => onSecret(event.target.checked)}
                    aria-label={`Sigiloso: ${label}`}
                />
                <EyeOff className="h-3.5 w-3.5 text-[#8a8a94]" aria-hidden="true" />
                Sigiloso
            </label>
            <span className="flex shrink-0 items-center">
                <button
                    type="button"
                    aria-label={`Subir ${label}`}
                    disabled={index === 0}
                    onClick={() => onMove(-1)}
                    className="rounded-md p-1.5 text-[#8a8a94] hover:bg-[#f2f2f5] hover:text-[#1d1d1f] disabled:opacity-30"
                >
                    <ArrowUp className="h-4 w-4" aria-hidden="true" />
                </button>
                <button
                    type="button"
                    aria-label={`Descer ${label}`}
                    disabled={index === count - 1}
                    onClick={() => onMove(1)}
                    className="rounded-md p-1.5 text-[#8a8a94] hover:bg-[#f2f2f5] hover:text-[#1d1d1f] disabled:opacity-30"
                >
                    <ArrowDown className="h-4 w-4" aria-hidden="true" />
                </button>
                <button
                    type="button"
                    aria-label={`Remover ${label}`}
                    onClick={onRemove}
                    className="rounded-md p-1.5 text-[#8a8a94] hover:bg-[#fdf3f1] hover:text-[#c1341a]"
                >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
            </span>
        </li>
    );
}

// Pendência que a esteira já viu: o cliente resolve aqui, antes de pagar, em vez de receber pendência depois.
function PendencyRow({ pendency, resolution, uploading, busy, onAttach, onAuthorize, onUndo }) {
    if (pendency.tipo === 'MIDIA')
        return (
            <li className="flex items-start gap-3 rounded-lg border border-[#ececf0] bg-[#fafafb] px-4 py-3 text-sm">
                <Film className="mt-0.5 h-4 w-4 shrink-0 text-[#8a8a94]" aria-hidden="true" />
                <span>
                    <span className="font-medium">{pendency.descricao}</span>
                    <span className="block text-xs text-[#6b6b76]">
                        Se precisar, junte pelo tribunal depois.
                    </span>
                </span>
            </li>
        );

    if (pendency.tipo === 'PROCURACAO')
        return (
            <li
                className={cx(
                    'flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3 text-sm',
                    resolution ? 'border-[#ececf0] bg-white' : 'border-[#f3d9b1] bg-[#fdf6ec]'
                )}
            >
                {resolution ? (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-[#1f7a4f]" aria-hidden="true" />
                ) : (
                    <AlertTriangle className="h-4 w-4 shrink-0 text-[#9a5512]" aria-hidden="true" />
                )}
                <span className="min-w-0 flex-1">
                    <span className="font-medium">Procuração</span>
                    <span className="block text-xs text-[#6b6b76]">
                        {resolution
                            ? 'Anexada.'
                            : pendency.descricao || 'A procuração não está nos autos.'}
                    </span>
                </span>
                {!resolution && (
                    <ActionButton
                        size="sm"
                        variant="secondary"
                        icon={UploadCloud}
                        loading={uploading}
                        disabled={busy && !uploading}
                        onClick={onAttach}
                        aria-label="Anexar: Procuração"
                    >
                        Anexar
                    </ActionButton>
                )}
            </li>
        );

    const done = !!resolution;

    return (
        <li
            className={cx(
                'flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3 text-sm',
                done ? 'border-[#ececf0] bg-white' : 'border-[#f3d9b1] bg-[#fdf6ec]'
            )}
        >
            {done ? (
                <CheckCircle2 className="h-4 w-4 shrink-0 text-[#1f7a4f]" aria-hidden="true" />
            ) : (
                <Paperclip className="h-4 w-4 shrink-0 text-[#9a5512]" aria-hidden="true" />
            )}
            <span className="min-w-0 flex-1">
                <span className="font-medium">{pendency.descricao}</span>
                <span className="block text-xs text-[#6b6b76]">
                    {resolution === 'attached'
                        ? 'Anexado.'
                        : resolution === 'authorized'
                        ? 'Você autorizou seguir sem este documento.'
                        : 'A peça cita este documento, mas ele não está nos anexos.'}
                </span>
            </span>
            {done ? (
                resolution === 'authorized' && (
                    <ActionButton size="sm" variant="ghost" icon={RotateCcw} onClick={onUndo}>
                        Desfazer
                    </ActionButton>
                )
            ) : (
                <span className="flex gap-2">
                    <ActionButton
                        size="sm"
                        variant="secondary"
                        icon={UploadCloud}
                        loading={uploading}
                        disabled={busy && !uploading}
                        onClick={onAttach}
                        aria-label={`Anexar: ${pendency.descricao}`}
                    >
                        Anexar
                    </ActionButton>
                    <ActionButton
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        onClick={onAuthorize}
                        aria-label={`Seguir sem: ${pendency.descricao}`}
                    >
                        Seguir sem
                    </ActionButton>
                </span>
            )}
        </li>
    );
}

export default function RequestFilingModal({
    open,
    onClose,
    requestId,
    panel,
    onReload,
    onFiled,
    onNoBalance
}) {
    const verbs = filingVerbs(panel?.type);
    const finalRef = useRef(null);
    const attachRef = useRef(null);

    const [documentChoice, setDocumentChoice] = useState('delivered');
    const [finalFile, setFinalFile] = useState(null);
    const [finalUrl, setFinalUrl] = useState(null);
    const [pendingActivations, setPendingActivations] = useState([]);
    const [uploadedFinal, setUploadedFinal] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [courtKey, setCourtKey] = useState('');
    const [uploadingMissing, setUploadingMissing] = useState(null);
    const [groupChoices, setGroupChoices] = useState({});
    const [requirementDocuments, setRequirementDocuments] = useState({});
    const [feeSlipNumber, setFeeSlipNumber] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState(null);
    // F261: a API recusou por campos da peça sem preencher; a cliente pode confirmar que está completa.
    const [blankFieldsRefused, setBlankFieldsRefused] = useState(false);
    const [connectTarget, setConnectTarget] = useState(null);
    const [connectLoading, setConnectLoading] = useState(null);
    const [attachments, setAttachments] = useState([]);
    const [removedIds, setRemovedIds] = useState([]);
    // descrição da pendência → 'authorized' ou { attachedId }: vale enquanto o anexo estiver na lista final.
    const [resolutions, setResolutions] = useState({});
    // F268: exigidos dispensados pelo cliente (chave do requisito ou do grupo) e a confirmação da lista escolhida.
    const [waivedKeys, setWaivedKeys] = useState([]);
    // A confirmação vale para a lista que o cliente viu: mudou a lista, desmarca (guarda a chave do que foi confirmado).
    const [acknowledgedKey, setAcknowledgedKey] = useState(null);
    // A API recusou com LISTA_NAO_CONFIRMADA (a lista mudou depois de abrir a tela): mostra o que ela apontou.
    const [serverList, setServerList] = useState(null);
    // Anexados aqui: a sugestão da API só lista documento ligado a exigência ou citação, então guardamos os nossos.
    const [localDocuments, setLocalDocuments] = useState([]);
    // Recusa lawyerNotEnabled: a API diz quem não está habilitado, mesmo que o painel ainda não soubesse.
    const [notEnabledIds, setNotEnabledIds] = useState([]);
    const [now, setNow] = useState(() => Date.now());
    // Meu INSS: a senha gov.br vive só aqui, até o envio; nunca vai para o painel, storage ou log.
    const [seguradoCpf, setSeguradoCpf] = useState('');
    const [govbrPassword, setGovbrPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [inssService, setInssService] = useState(INSS_AUTO);
    const [cpfError, setCpfError] = useState(null);
    const [procuradorMissing, setProcuradorMissing] = useState(false);

    useEffect(() => {
        if (!finalFile) { setFinalUrl(null); return; }
        const url = URL.createObjectURL(finalFile);
        setFinalUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [finalFile]);

    const missing = panel?.missing || [];
    const caseData = panel?.case || null;
    const pieceId = panel?.deliveredDocument?.id;
    // Painel antigo (sem a lista final) segue como antes: a API monta os anexos sozinha.
    const hasDocumentList = Array.isArray(panel?.documents);
    const canSelectRequirements = hasDocumentList && panel?.supportsRequirementDocuments === true;
    const panelDocumentIds = new Set(
        (panel?.documents || []).map((document) => Number(document?.id))
    );
    const localIds = localDocuments.map((document) => Number(document.id));
    const allDocuments = hasDocumentList
        ? [
              ...panel.documents,
              ...localDocuments.filter((document) => !panelDocumentIds.has(Number(document.id)))
          ]
        : panel?.documents;
    const removedSet = new Set(removedIds.map(Number));
    // Anexo removido da lista final deixa de valer para o documento obrigatório que ele atendia.
    const destination = panel?.court || (panel?.courtOptions || []).find((court) => court.key === courtKey);
    const courtSystem = destination?.system;
    const meuInss = isMeuInss(destination);
    const availableDocuments = sendableDocuments(allDocuments, pieceId);
    const requirements = (panel?.requirements || [])
        // F269: a opção sem arquivo vale por tribunal (allowedCourts); a API antiga só mandava o sistema.
        .filter((item) =>
            item.allowedCourts
                ? item.allowedCourts.includes(destination?.key)
                : !item.allowedSystems || item.allowedSystems.includes(courtSystem)
        )
        .map((item) => ({
            ...item,
            documents: (Object.hasOwn(requirementDocuments, item.key)
                ? requirementDocuments[item.key].map((id) => availableDocuments.find((document) => Number(document.id) === id)).filter(Boolean)
                : item.documents || []).filter((document) => !removedSet.has(Number(document.id)))
        }));
    // Grupos de alternativas (ex.: custas = guia paga OU justiça gratuita): o cliente escolhe uma. Começa pela
    // que já tem arquivo; sem nada, pela justiça gratuita se o cadastro pediu, ou pela primeira da lista.
    const groups = [
        ...new Set(
            requirements
                .filter((item) => item.alternativeGroup)
                .map((item) => item.alternativeGroup)
        )
    ].map((group) => {
        const items = requirements.filter((item) => item.alternativeGroup === group);
        const alternatives = [...new Set(items.map((item) => item.alternative))];
        const withFiles = alternatives.find((alternative) =>
            items.some((item) => item.alternative === alternative && item.documents?.length)
        );
        const fromRegistration =
            group === 'fees' && panel?.freeJustice && alternatives.includes('free') ? 'free' : null;
        const chosen = alternatives.includes(groupChoices[group])
            ? groupChoices[group]
            : withFiles || fromRegistration || alternatives[0];
        const chosenItems = items.filter((item) => item.alternative === chosen);
        return {
            group,
            items,
            alternatives,
            chosen,
            chosenItems,
            chosenHasFile: chosenItems.some((item) => item.noFile || item.documents?.length)
        };
    });
    const asksFeeSlipNumber = groups.some(({ items, chosen }) =>
        items.some((item) => item.alternative === chosen && item.askFeeSlipNumber)
    );
    const courtOptions = panel?.courtOptions || [];
    const chosenGroup = courtOptions.find((court) => court.key === courtKey)?.accessGroup;
    const signerGroup = chosenGroup || panel?.court?.accessGroup;
    // Distribuição: quem pode assinar depende do tribunal escolhido.
    const signers = signersWithEnablement(
        (panel?.signers || []).map((signer) =>
            chosenGroup && signer.byAccessGroup?.[chosenGroup]
                ? { ...signer, ...signer.byAccessGroup[chosenGroup] }
                : signer
        ),
        panel?.lawyers
    ).map((signer) =>
        notEnabledIds.includes(Number(signer.certificateId))
            ? { ...signer, habilitado: false }
            : signer
    );

    const activating = signers.some(signer => signer.reasonCode === 'CODIGO_EM_ATIVACAO');
    useEffect(() => {
        if (!open || !activating) return;
        const timer = setInterval(() => onReload?.(), 10000);
        return () => clearInterval(timer);
    }, [open, activating, onReload]);

    useEffect(() => {
        if (!open) return;
        const completed = pendingActivations.filter(item => item.accessGroup === signerGroup &&
            signers.some(signer => Number(signer.certificateId) === Number(item.certificateId) && signer.ready));
        if (!completed.length) return;
        setSelectedIds(current => [...new Set([...current, ...completed.map(item => item.certificateId)])]);
        setPendingActivations(current => current.filter(item => !completed.includes(item)));
    }, [open, signers, signerGroup, pendingActivations]);

    const readyFor = (group) =>
        (panel?.signers || []).filter((signer) =>
            group && signer.byAccessGroup?.[group]
                ? signer.byAccessGroup[group].ready
                : signer.ready
        );

    // Abrir ou fechar a tela apaga a senha gov.br.
    useEffect(() => {
        setGovbrPassword('');
        setShowPassword(false);
    }, [open]);

    useEffect(() => {
        if (!open) return;

        // Peça e versão final ficam como estavam: quem volta depois de renovar o saldo não reenvia o arquivo.
        setError(null);
        setNow(Date.now());
        // Tribunal sugerido pelos dados da solicitação; o cliente troca se quiser.
        const suggested = courtOptions.find((court) => court.key === panel?.suggestedCourtKey);
        setCourtKey(suggested?.key || '');
        const ready = readyFor(suggested?.accessGroup);
        setSelectedIds(ready.length === 1 ? [ready[0].certificateId] : []);
        setRemovedIds([]);
        setResolutions({});
        setWaivedKeys([]);
        setAcknowledgedKey(null);
        setServerList(null);
        setGroupChoices({});
        setRequirementDocuments({});
        setNotEnabledIds([]);
        setSeguradoCpf('');
        setInssService(INSS_AUTO);
        setCpfError(null);
        setProcuradorMissing(false);
        setAttachments(
            mergeAttachments([], allDocuments, { pieceId, included: localIds, caseData })
        );
        // O prazo e o horário valem no momento do pedido: busca o painel de novo ao abrir.
        onReload?.();
    }, [open]);

    // Recarga do painel (anexo novo, acesso conectado): o que o cliente já ordenou fica; o novo entra no fim.
    useEffect(() => {
        if (open)
            setAttachments((current) =>
                mergeAttachments(current, allDocuments, {
                    pieceId,
                    removed: removedIds,
                    included: localIds,
                    caseData
                })
            );
    }, [panel?.documents, localDocuments]);

    // Custas: troca de opção leva os arquivos certos para a lista final.
    useEffect(() => {
        if (!open || !hasDocumentList || !groups.length) return;
        const byId = new Map(
            sendableDocuments(allDocuments, pieceId).map((document) => [
                Number(document.id),
                document
            ])
        );
        setAttachments((current) =>
            syncAlternatives(current, groups, {
                removed: removedIds,
                preserved: [
                    ...requirements.filter((item) => !item.alternativeGroup)
                        .flatMap((item) => item.documents.map((document) => document.id)),
                    ...(caseData?.documentosCitados || []).map((item) => item.documentId),
                    ...(allDocuments || []).filter((document) => document.citedAs).map((document) => document.id)
                ],
                caseData,
                documentsById: byId
            })
        );
    }, [open, courtKey, groupChoices, requirementDocuments, panel?.requirements, panel?.documents]);

    // Com a tela aberta, o relógio de Brasília anda: às 20h (e à meia-noite do dia do prazo) o aviso e o botão mudam sozinhos.
    useEffect(() => {
        if (!open) return undefined;
        const timer = setInterval(() => setNow(Date.now()), 30 * 1000);
        return () => clearInterval(timer);
    }, [open]);

    const chooseCourt = (key) => {
        setCourtKey(key);
        // A senha é do destino escolhido: trocar de destino apaga.
        setGovbrPassword('');
        setShowPassword(false);
        const group = courtOptions.find((court) => court.key === key)?.accessGroup;
        const ready = readyFor(group);
        const stillReady = selectedIds.filter((id) =>
            ready.some((signer) => signer.certificateId === id)
        );
        setSelectedIds(
            stillReady.length ? stillReady : ready.length === 1 ? [ready[0].certificateId] : []
        );
    };

    if (!panel) return null;

    // A contagem de páginas da peça vem na lista da API (main_piece), não em deliveredDocument.
    const mainEntry = (panel.documents || []).find(
        (document) =>
            document?.documentType === 'main_piece' || Number(document?.id) === Number(pieceId)
    );
    const delivered = panel.deliveredDocument
        ? {
              ...panel.deliveredDocument,
              pages: panel.deliveredDocument.pages ?? mainEntry?.pages ?? mainEntry?.pageCount
          }
        : null;

    const deadline = deadlineState(panel.deadline, now);
    const summary = caseSummary(caseData);
    const pendencies = pendenciesOf(panel);
    const missingCited = pendencies.filter((item) => item.tipo === 'DOCUMENTO_FALTANTE');
    const attachedIds = new Set(attachments.map((item) => Number(item.id)));
    // Anexo que supre o citado, de uma abertura anterior (citedAs gravado no upload).
    const citedKey = (text) =>
        String(text || '')
            .trim()
            .replace(/\s+/g, ' ')
            .toLowerCase();
    const citedAttachmentOf = (descricao) =>
        (allDocuments || []).find(
            (document) =>
                document?.citedAs &&
                citedKey(document.citedAs) === citedKey(descricao) &&
                (!hasDocumentList || attachedIds.has(Number(document.id)))
        );
    // Anexado só conta enquanto o arquivo estiver na lista final (painel antigo: não há lista para tirar).
    const resolutionOf = (descricao) => {
        const resolution = resolutions[descricao];
        if (resolution === 'authorized') return 'authorized';
        if (
            resolution?.attachedId &&
            (!hasDocumentList || attachedIds.has(Number(resolution.attachedId)))
        )
            return 'attached';
        if (!resolution && citedAttachmentOf(descricao)) return 'attached';
        return null;
    };
    const unresolvedCited = missingCited.filter((item) => !resolutionOf(item.descricao));

    const documentsById = new Map(
        sendableDocuments(allDocuments, pieceId).map((document) => [Number(document.id), document])
    );
    // Fora do envio: o que o cliente tirou e os documentos da solicitação que a API não sugeriu.
    const removedDocuments = [
        ...removedIds.map((id) => documentsById.get(Number(id))).filter(Boolean),
        ...[...documentsById.values()].filter(
            (document) =>
                document.selected === false &&
                !attachedIds.has(Number(document.id)) &&
                !removedSet.has(Number(document.id))
        )
    ];
    const piece = documentChoice === 'final' ? { name: finalFile?.name, pages: null, url: finalFile ? finalUrl : null } : delivered;
    const pages = totalPages([
        piece || {},
        ...attachments.map((item) => documentsById.get(Number(item.id)) || {})
    ]);

    const hasReadySigner = signers.some((signer) => signer.ready);
    const needsCourt = !panel.court && courtOptions.length > 0;
    // Distribuição sem tribunal escolhido: ainda não dá para dizer quem está conectado a ele.
    const courtPending = needsCourt && !courtKey;
    const chosenSigners = selectedIds
        .map((id) => signers.find((signer) => Number(signer.certificateId) === Number(id)))
        .filter((signer) => signer?.ready);
    const notEnabledChosen = chosenSigners.filter((signer) => signer.habilitado === false);
    // A opção escolhida em cada grupo (ex.: justiça gratuita) precisa ter arquivo: a outra não vale por ela.
    const isWaived = (key) => waivedKeys.includes(key);
    const groupWithoutFile = groups.some(
        ({ group, chosenItems, chosenHasFile }) =>
            !chosenHasFile && !isWaived(group) && chosenItems.some((item) => item.required)
    );
    const requiredRemoved = requirements.some(
        (item) =>
            item.required &&
            !item.alternativeGroup &&
            !item.documents.length &&
            !isWaived(item.key)
    );
    // F268: a lista é do cliente. Tirou o que sugerimos, dispensou um exigido ou deixou prova citada sem arquivo: ele
    // confirma que essa escolha é dele (a API grava a confirmação com o pedido).
    const waivedNow = [
        ...requirements.filter((item) => !item.alternativeGroup && item.waivable && isWaived(item.key) && !item.documents.length)
            .map((item) => ({ key: item.key, label: item.label })),
        ...groups.filter(({ group, items, chosenHasFile }) => isWaived(group) && !chosenHasFile && items.every((item) => item.waivable || !item.required))
            .map(({ group, items }) => ({ key: group, label: items.map((item) => item.label).join(' ou ') }))
    ];
    // A troca de alternativa (guia paga × justiça gratuita) não é "tirar" documento: o arquivo da outra opção não conta.
    const otherAlternativeIds = new Set(
        groups.flatMap(({ items, chosen }) =>
            items.filter((item) => item.alternative !== chosen).flatMap((item) => (item.documents || []).map((document) => Number(document.id)))
        )
    );
    const suggestedRemoved = hasDocumentList
        ? (panel.documents || []).filter(
              (document) =>
                  document?.selected === true &&
                  !otherAlternativeIds.has(Number(document.id)) &&
                  Number(document.id) !== Number(pieceId) &&
                  document.documentType !== 'main_piece' &&
                  !attachedIds.has(Number(document.id))
          )
        : [];
    const citedWithoutFile = requirements.filter((item) => item.cited && !item.documents.length);
    const unique = (names) => [...new Set(names.filter(Boolean))];
    const listSummary = {
        removed: unique([...suggestedRemoved.map((document) => document.name), ...(serverList?.removed || []).map((item) => item?.name)]),
        waived: unique([...waivedNow.map((item) => item.label), ...(serverList?.waived || []).map((item) => item?.label)]),
        withoutFile: unique([...citedWithoutFile.map((item) => item.label), ...(serverList?.withoutFile || [])])
    };
    const needsListAcknowledgement = !!(listSummary.removed.length || listSummary.waived.length || listSummary.withoutFile.length);
    const listKey = JSON.stringify([listSummary, attachments.map((item) => Number(item.id))]);
    const listAcknowledged = acknowledgedKey === listKey;
    const toggleWaived = (key) =>
        setWaivedKeys((current) => (current.includes(key) ? current.filter((entry) => entry !== key) : [...current, key]));
    const pieceMissing = documentChoice === 'final' ? !finalFile : !panel.deliveredDocument;

    const termMissing = panel.termAccepted === false;
    const noBalance = !!panel.balance && panel.balance.enough === false;

    // Prazo vencido ou dia do prazo (aceitamos até as 23h59 da véspera): não há o que o cliente faça aqui, o botão fica
    // desativado.
    const hardStop = deadline.overdue
        ? 'Prazo vencido: não fazemos o pedido fora do prazo.'
        : deadline.tooClose
        ? 'Aceitamos o pedido até as 23h59 da véspera do prazo.'
        : null;

    const fontNotice = fontNoticeNames(panel.fontNotice);
    const finalFileIsPdf = /\.pdf$/i.test(finalFile?.name || '');
    const finalIsPdf = documentChoice === 'final' && finalFileIsPdf;
    const finalLinkProps = {
        target: finalFileIsPdf ? '_blank' : undefined,
        download: finalFileIsPdf ? undefined : finalFile?.name,
    };

    const procurador = panel.meuInss || {};
    const procuradorBlocked = meuInss && (procurador.procuradorReady === false || procuradorMissing);

    const blockers = [
        termMissing && 'ative o protocolo em Minha conta',
        needsCourt && !courtKey && 'escolha o tribunal',
        !meuInss && !signers.length && 'envie um certificado',
        !meuInss && !!signers.length && !hasReadySigner && 'conecte o tribunal para quem vai assinar',
        !meuInss && hasReadySigner && !chosenSigners.length && 'escolha quem assina',
        !meuInss && notEnabledChosen.length > 0 && 'tire quem não está habilitado no processo',
        procuradorBlocked && 'complete o CPF do advogado na sua conta',
        meuInss && (!isValidCpf(seguradoCpf) || !govbrPassword) && 'informe o CPF e a senha gov.br do segurado',
        (missing.some((item) => !requirements.some((rule) => rule.key === item.key || rule.alternativeGroup === item.key)) || groupWithoutFile || requiredRemoved) &&
            'anexe os documentos obrigatórios',
        pieceMissing && (documentChoice === 'final' ? 'anexe a versão final' : 'anexe a peça'),
        unresolvedCited.length > 0 && 'resolva os documentos citados que faltam',
        needsListAcknowledgement && !listAcknowledged && 'confirme a lista de documentos',
        // O anexo ainda subindo ficaria fora da lista enviada.
        !!uploadingMissing && 'espere o anexo terminar de subir'
    ].filter(Boolean);

    const fee = Number(panel.fee) || 0;
    const feeText =
        fee > 0
            ? `${formatMoney(fee)} por ${verbs.noun}, debitado do seu pacote.`
            : 'Grátis no seu plano.';
    const footerText =
        hardStop ||
        (blockers.length
            ? `Para continuar, ${blockers.join(', ')}.`
            : noBalance
            ? `Seu pacote tem ${formatMoney(panel.balance.remaining)} e ${verbs.theNoun} custa ${formatMoney(fee)}. Renove para continuar: a peça fica pronta aqui.`
            : deadline.nextBusinessDay
            ? `${verbs.We} no próximo dia útil, a partir das 8h, e avisamos a cada etapa. ${feeText}`
            : `${verbs.We} agora e avisamos a cada etapa. ${feeText}`);

    const courtShort = (
        courtOptions.find((court) => court.key === courtKey)?.name ||
        panel.court?.name ||
        ''
    ).split(' · ')[0];
    const blocked = !!blockers.length || !!hardStop;
    const confirmLabel = `Confirmar e ${verbs.action.toLowerCase()}${
        courtShort ? ` no ${courtShort}` : ''
    }`;

    const groupNeedsTotp =
        (panel.courtOptions || []).find((option) => option.accessGroup === signerGroup)
            ?.requiresTotp ??
        panel.court?.requiresTotp ??
        true;

    const toggleSigner = (id) =>
        setSelectedIds((current) =>
            current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
        );
    const makePrincipal = (id) =>
        setSelectedIds((current) => [id, ...current.filter((item) => item !== id)]);

    const openConnect = async (signer) => {
        setConnectLoading(signer.certificateId);
        setError(null);
        try {
            const settings = await filingService.findSettings();
            const access = (settings.accessGroups || []).find((group) => group.key === signerGroup);
            const certificate = (settings.lawyers || [])
                .map((lawyer) => lawyer.certificate)
                .find((item) => Number(item?.id) === Number(signer.certificateId));
            if (!access || !certificate) throw new Error('ACCESS_NOT_FOUND');
            // Tribunal sem 2FA: adicionar à lista do advogado já basta.
            if (!access.requiresTotp) {
                await filingService.connectAccess({
                    certificateId: certificate.id,
                    accessKey: access.key
                });
                await onReload();
                setSelectedIds((current) =>
                    current.includes(certificate.id) ? current : [...current, certificate.id]
                );
                return;
            }
            if (certificate.access?.[access.key]?.status === 'ready') {
                await onReload();
                setSelectedIds(current => current.includes(certificate.id) ? current : [...current, certificate.id]);
                return;
            }
            setConnectTarget({ access, certificate });
        } catch (failure) {
            setError('Não conseguimos abrir a conexão agora. Tente em Minha conta › Certificado e tribunais.');
        } finally {
            setConnectLoading(null);
        }
    };

    // key: o que pediu o anexo (documento obrigatório, citado na peça ou anexo avulso).
    const sendAttachment = async (target, file) => {
        if (!file || !target) return;

        setUploadingMissing(target.uploadKey || target.key);
        setError(null);
        try {
            const extra = target.cited
                ? { additionalInformation: { citedAs: target.cited } }
                : target.nameType
                ? { nameType: target.nameType }
                : {};
            const uploaded = await uploadOne(requestId, file, extra);
            const id = Number(uploaded.id);
            if (hasDocumentList) {
                setLocalDocuments((current) => [
                    ...current,
                    {
                        id,
                        name: uploaded.name || file.name,
                        documentType: 'other',
                        order: Number.MAX_SAFE_INTEGER
                    }
                ]);
                setRemovedIds((current) => current.filter((item) => Number(item) !== id));
            }
            if (target.uploadKey && canSelectRequirements)
                selectRequirement(target.item, id, { id, name: uploaded.name || file.name });
            if (target.pendency)
                setResolutions((current) => ({
                    ...current,
                    [target.pendency]: { attachedId: id }
                }));
            await onReload();
        } catch (failure) {
            setError('Não conseguimos anexar esse arquivo. Confira a internet e tente de novo.');
        } finally {
            setUploadingMissing(null);
        }
    };

    const pick = (target) => {
        attachRef.current.target = target;
        attachRef.current.click();
    };

    const pickMissing = (item) =>
        pick({ key: 'requirement', uploadKey: item.key, nameType: item.nameType || item.key, item });

    const selectRequirement = (item, id, uploaded = null) => {
        const document = uploaded || availableDocuments.find((entry) => Number(entry.id) === id);
        const ids = document
            ? [...new Set([...(item.multiple ? item.documents.map((entry) => Number(entry.id)) : []), id])]
            : [];
        const replaced = item.multiple ? [] : item.documents.map((entry) => Number(entry.id)).filter((entry) =>
            !ids.includes(entry) &&
            !requirements.some((other) => other.key !== item.key && other.documents.some((document) => Number(document.id) === entry)) &&
            !caseData?.documentosCitados?.some((document) => Number(document.documentId) === entry) &&
            !availableDocuments.find((document) => Number(document.id) === entry)?.citedAs
        );
        setRequirementDocuments((current) => ({ ...current, [item.key]: ids }));
        setRemovedIds((current) => [...new Set([...current, ...replaced])].filter((entry) => !ids.includes(Number(entry))));
        setAttachments((current) => current.filter((entry) => !replaced.includes(Number(entry.id))));
        if (document)
            setAttachments((current) => current.some((entry) => Number(entry.id) === id)
                ? current : [...current, { id, secret: secretSuggestion(document, caseData) }]);
    };

    const removeAttachment = (id) => {
        setAttachments((current) => current.filter((item) => item.id !== id));
        setRemovedIds((current) => [...current, id]);
    };

    const restoreAttachment = (id) => {
        setRemovedIds((current) => current.filter((item) => Number(item) !== Number(id)));
        const document = documentsById.get(Number(id));
        setAttachments((current) => [
            ...current,
            { id: Number(id), secret: !!document && secretSuggestion(document, caseData) }
        ]);
    };

    // confirmBlankFields: reenvio depois da recusa PECA_CAMPOS_EM_BRANCO (o clique no botão manda o evento: só true vale).
    const submit = async ({ confirmBlankFields } = {}) => {
        if (blockers.length || hardStop) return;

        if (noBalance) return onNoBalance({ fee: panel.fee, remaining: panel.balance.remaining });

        setIsSending(true);
        setError(null);
        setBlankFieldsRefused(false);

        try {
            let finalDocument = null;
            if (documentChoice === 'final') {
                finalDocument =
                    uploadedFinal && sameFile(uploadedFinal.file, finalFile)
                        ? uploadedFinal.document
                        : await uploadOne(requestId, finalFile);
                setUploadedFinal({ file: finalFile, document: finalDocument });
            }
            const authorizedMissing = authorizedMissingOf(
                missingCited
                    .filter((item) => resolutionOf(item.descricao) === 'authorized')
                    .map((item) => item.descricao)
            );
            const selectedRequirements = requirements.filter((item) =>
                Object.hasOwn(requirementDocuments, item.key) &&
                (!item.alternativeGroup || groups.some((group) =>
                    group.group === item.alternativeGroup && group.chosen === item.alternative
                ))
            );
            const selections = Object.fromEntries(selectedRequirements.map((item) => [
                item.key, item.documents.map((document) => Number(document.id))
            ]));
            const created = await filingService.requestFiling(requestId, {
                certificateIds: meuInss ? undefined : chosenSigners.map((signer) => signer.certificateId),
                certificateId: meuInss ? undefined : chosenSigners[0]?.certificateId,
                segurado: meuInss ? { cpf: cpfDigits(seguradoCpf), govbrPassword } : undefined,
                inssService: meuInss && inssService !== INSS_AUTO ? inssService : undefined,
                courtKey: needsCourt ? courtKey : undefined,
                finalDocumentId: finalDocument?.id,
                feeSlipNumber: asksFeeSlipNumber
                    ? feeSlipNumber.replace(/\D/g, '') || undefined
                    : undefined,
                requirementDocuments: selectedRequirements.length ? selections : undefined,
                alternatives: groups.length
                    ? Object.fromEntries(groups.map(({ group, chosen }) => [group, chosen]))
                    : undefined,
                documents: hasDocumentList
                    ? attachments.map((item, index) => ({
                          id: item.id,
                          order: index + 1,
                          secret: !!item.secret
                      }))
                    : undefined,
                authorizedMissing: authorizedMissing.length ? authorizedMissing : undefined,
                waivedRequirements: waivedNow.length ? waivedNow.map((item) => item.key) : undefined,
                listAcknowledged: needsListAcknowledgement && listAcknowledged ? true : undefined,
                confirmDeadline: deadline.dueToday || deadline.dueTomorrow || undefined,
                confirmBlankFields: confirmBlankFields === true || undefined
            });
            onFiled(created);
        } catch (failure) {
            const code = filingService.errorCodeOf(failure);
            const body = failure?.response?.data || {};

            if (BALANCE_ERRORS.includes(code))
                return onNoBalance({
                    fee: body.fee ?? panel.fee,
                    remaining: body.remaining ?? panel.balance?.remaining
                });

            if (code === 'PEDIDO_JA_EXISTE') return onFiled(body);

            if (code === 'PECA_CAMPOS_EM_BRANCO') setBlankFieldsRefused(true);

            if (code === 'lawyerNotEnabled' && Array.isArray(body.certificateIds))
                setNotEnabledIds(body.certificateIds.map(Number));

            // O painel ficou velho (anexo apagado, acesso pedindo reconexão, prazo, habilitação): recarrega
            // para mostrar o que falta em vez de deixar o cliente tentando de novo.
            if (STALE_PANEL_ERRORS.includes(code)) await onReload();

            // F268: a API viu na lista algo que a tela não mostrava (documento novo, versão final): mostra e pede a confirmação.
            if (code === 'LISTA_NAO_CONFIRMADA') {
                setServerList({ removed: body.removed || [], waived: body.waived || [], withoutFile: body.withoutFile || [] });
                setAcknowledgedKey(null);
            }

            if (code === 'CPF_INVALIDO') return setCpfError(CPF_INVALID);
            if (code === 'PROCURADOR_SEM_CPF') setProcuradorMissing(true);

            const offline = failure?.message !== 'UPLOAD_FAILED' && !failure?.response;
            if (offline) onReload();
            setError(
                REFUSALS[code] ||
                    (code === 'PROCURADOR_SEM_CPF' && PROCURADOR_MISSING) ||
                    (code === 'SERVICO_INSS_INVALIDO' && INSS_SERVICE_INVALID) ||
                    (offline && NETWORK_ERROR) ||
                    (failure?.message === 'UPLOAD_FAILED'
                        ? 'Não conseguimos enviar a sua versão final. Confira a internet e tente de novo.'
                        : filingService.errorMessageOf(failure))
            );
        } finally {
            setIsSending(false);
            setGovbrPassword('');
            setShowPassword(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            dismissible={!isSending}
            icon={Stamp}
            size="lg"
            title={`Conferir e ${verbs.action.toLowerCase()}`}
            description="Confira os dados abaixo. Depois do pedido, a gente cuida do resto e te avisa a cada etapa."
            footer={
                blocked ? (
                    // Travado: o aviso ocupa o lugar do botão de confirmar.
                    <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center">
                        <Notice
                            tone={hardStop ? 'danger' : 'warning'}
                            icon={AlertTriangle}
                            className="flex-1"
                        >
                            <span data-testid="filing-footer-block">{footerText}</span>
                        </Notice>
                        <ActionButton variant="secondary" onClick={onClose} className="shrink-0">
                            Fechar
                        </ActionButton>
                    </div>
                ) : (
                    <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="m-0 text-[13px] text-[#6b6b76]">{footerText}</p>
                        <div className="flex shrink-0 flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
                            <ActionButton variant="secondary" onClick={onClose} disabled={isSending}>
                                Cancelar
                            </ActionButton>
                            <ActionButton
                                icon={noBalance ? Wallet : Stamp}
                                className="whitespace-nowrap"
                                loading={isSending}
                                onClick={submit}
                            >
                                {noBalance ? 'Renovar pacote' : confirmLabel}
                            </ActionButton>
                        </div>
                    </div>
                )
            }
        >
            <div className="flex flex-col gap-6">
                {termMissing && (
                    <Notice
                        tone="warning"
                        icon={AlertTriangle}
                        action={
                            <Link
                                to="/profile#filing"
                                className="text-sm font-medium text-primary hover:underline"
                            >
                                Ativar
                            </Link>
                        }
                    >
                        Antes do primeiro pedido, o titular da conta autoriza o protocolo e aceita o
                        preço em Minha conta. Leva um minuto.
                    </Notice>
                )}

                {noBalance && (
                    <Notice tone="warning" icon={AlertTriangle} title="Saldo insuficiente">
                        {`${verbs.theNoun[0].toUpperCase()}${verbs.theNoun.slice(
                            1
                        )} custa ${formatMoney(panel.fee)} e o seu pacote tem ${formatMoney(
                            panel.balance.remaining
                        )}. Renove antes de confirmar: a peça continua pronta.`}
                    </Notice>
                )}

                <section className="flex flex-col gap-3 rounded-xl border border-[#ececf0] bg-[#fafafb] px-4 py-3">
                    <DeadlineLine state={deadline} source={summary.deadlineSource} />
                    {deadline.overdue && (
                        <Notice tone="danger" icon={AlertTriangle} title="Prazo vencido">
                            {`O prazo terminou em ${
                                formatDay(deadline.day) || 'data anterior'
                            }. Não fazemos ${verbs.theNoun} fora do prazo por aqui.`}
                        </Notice>
                    )}
                    {deadline.tooClose && (
                        <Notice tone="danger" icon={Clock}>
                            {`Aceitamos o pedido até as 23h59 da véspera do prazo. Para não perder o prazo, ${verbs.imperative} direto no tribunal. Nada foi cobrado.`}
                        </Notice>
                    )}
                    {!deadline.overdue && !deadline.tooClose && deadline.dueTomorrow && (
                        <Notice tone="warning" icon={Clock}>
                            {`O prazo vence amanhã. ${verbs.We} se você confirmar até as 23h59 de hoje.`}
                        </Notice>
                    )}
                    {deadline.nextBusinessDay && (
                        <Notice tone="brand" icon={Clock}>
                            {deadline.afterHours
                                ? `Pedidos feitos após as 20h são ${verbs.donePlural} no próximo dia útil, a partir das 8h.`
                                : `${verbs.We} no próximo dia útil, a partir das 8h.`}
                        </Notice>
                    )}

                    <dl className="m-0 flex flex-col gap-1.5 border-t border-[#ececf0] pt-3">
                        {needsCourt ? (
                            <div className="flex flex-col gap-1.5">
                                <SelectInput
                                    id="filing-court"
                                    value={courtKey}
                                    onChange={chooseCourt}
                                    placeholder="Escolha onde distribuir"
                                    // F158: o sistema diz onde vale quando o tribunal tem dois (TJRJ eproc no cível, PJe nos Juizados)
                                    options={courtOptions.map((court) => ({
                                        value: court.key,
                                        name: court.scope ? `${court.name} — ${court.scope}` : court.name
                                    }))}
                                />
                                {!!panel.suggestedCourtKey &&
                                    courtKey === panel.suggestedCourtKey && (
                                        <p className="m-0 text-xs text-[#6b6b76]">
                                            Escolhemos pelo local da ação. Se for outro, é só
                                            trocar.
                                        </p>
                                    )}
                            </div>
                        ) : (
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
                                <Landmark className="h-4 w-4 text-[#8a8a94]" aria-hidden="true" />
                                <span className="font-medium">
                                    {panel.court?.name || NOT_INFORMED}
                                </span>
                                {/* Meu INSS: requerimento administrativo, sem número de processo. */}
                                {(!meuInss || panel.processNumber) && (
                                    <span
                                        className={
                                            panel.processNumber ? 'text-[#6b6b76]' : 'text-[#8a8a94]'
                                        }
                                    >
                                        ·{' '}
                                        {panel.processNumber ||
                                            (panel.type === 'distribution'
                                                ? 'processo novo'
                                                : `processo ${NOT_INFORMED}`)}
                                    </span>
                                )}
                            </div>
                        )}
                        {panel.type !== 'distribution' && !meuInss && (
                            <InfoLine label="Vara">{summary.vara}</InfoLine>
                        )}
                        <InfoLine label="Polo ativo">{summary.ativo}</InfoLine>
                        <InfoLine label="Polo passivo">{summary.passivo}</InfoLine>
                    </dl>
                </section>

                {pendencies.length > 0 && (
                    <Block title="Antes de confirmar">
                        <ul className="m-0 flex list-none flex-col gap-2 p-0">
                            {pendencies.map((pendency) => (
                                <PendencyRow
                                    key={`${pendency.tipo}-${pendency.descricao}`}
                                    pendency={pendency}
                                    resolution={resolutionOf(pendency.descricao)}
                                    uploading={
                                        uploadingMissing === `pendency-${pendency.descricao}`
                                    }
                                    busy={!!uploadingMissing}
                                    onAttach={() =>
                                        pick(
                                            pendency.tipo === 'PROCURACAO'
                                                ? {
                                                      key: `pendency-${pendency.descricao}`,
                                                      nameType: 'powerOfAttorney',
                                                      pendency: pendency.descricao
                                                  }
                                                : {
                                                      key: `pendency-${pendency.descricao}`,
                                                      cited: pendency.descricao,
                                                      pendency: pendency.descricao
                                                  }
                                        )
                                    }
                                    onAuthorize={() =>
                                        setResolutions((current) => ({
                                            ...current,
                                            [pendency.descricao]: 'authorized'
                                        }))
                                    }
                                    onUndo={() =>
                                        setResolutions((current) => {
                                            const next = { ...current };
                                            delete next[pendency.descricao];
                                            return next;
                                        })
                                    }
                                />
                            ))}
                        </ul>
                    </Block>
                )}

                <Block title="Peça">
                    <Choice
                        name="filing-document"
                        checked={documentChoice === 'delivered'}
                        onSelect={() => setDocumentChoice('delivered')}
                    >
                        <FileText className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                        <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium">
                                {delivered?.name || 'Peça entregue'}
                            </span>
                            <span className="block text-xs text-[#6b6b76]">
                                {[
                                    'Como entregamos',
                                    Number(delivered?.pages) > 0 ? `${delivered.pages} pág.` : null
                                ]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </span>
                        </span>
                        {panel.deliveredDocument?.url && (
                            <a
                                href={panel.deliveredDocument.url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs font-medium text-primary hover:underline"
                                onClick={(event) => event.stopPropagation()}
                            >
                                Abrir
                            </a>
                        )}
                    </Choice>
                    <Choice
                        name="filing-document"
                        checked={documentChoice === 'final'}
                        onSelect={() => setDocumentChoice('final')}
                    >
                        <Paperclip className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                        <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium">
                                Eu editei: usar minha versão final
                            </span>
                            <span className="block truncate text-xs text-[#6b6b76]">
                                {finalFile ? finalFile.name : 'PDF ou Word'}
                            </span>
                        </span>
                        {finalFile && finalUrl && (
                            <a href={finalUrl} {...finalLinkProps}
                                rel="noreferrer" className="text-xs font-medium text-primary hover:underline"
                                onClick={event => event.stopPropagation()}>Abrir</a>
                        )}
                        {documentChoice === 'final' &&
                            (finalFile ? (
                                <button
                                    type="button"
                                    aria-label="Remover arquivo"
                                    onClick={() => setFinalFile(null)}
                                    className="rounded-md p-1 text-[#8a8a94] hover:bg-white hover:text-[#1d1d1f]"
                                >
                                    <X className="h-4 w-4" aria-hidden="true" />
                                </button>
                            ) : (
                                <ActionButton
                                    size="sm"
                                    variant="secondary"
                                    icon={UploadCloud}
                                    onClick={() => finalRef.current?.click()}
                                >
                                    Escolher
                                </ActionButton>
                            ))}
                    </Choice>
                    <input
                        ref={finalRef}
                        type="file"
                        accept={FINAL_ACCEPT}
                        className="hidden"
                        onChange={(event) => {
                            setFinalFile(event.target.files?.[0] || null);
                            event.target.value = '';
                        }}
                    />
                    {/* Só informa: com a peça em PDF do cliente não há troca de fonte. */}
                    {fontNotice && !finalIsPdf && (
                        <p
                            data-testid="filing-font-notice"
                            className="m-0 flex items-start gap-2 text-xs leading-5 text-[#6b6b76]"
                        >
                            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                            <span>
                                {`Sua peça usa ${fontNotice}. No PDF protocolado usamos uma fonte equivalente; o conteúdo não muda. Se preferir o visual exato, `}
                                <button
                                    type="button"
                                    className="font-medium text-primary hover:underline"
                                    onClick={() => setDocumentChoice('final')}
                                >
                                    envie o PDF exportado do seu Word
                                </button>
                                .
                            </span>
                        </p>
                    )}
                </Block>

                {meuInss ? (
                    <Block title="Acesso ao Meu INSS do segurado">
                        <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
                            <Field label="CPF do segurado" htmlFor="filing-segurado-cpf" required error={cpfError}>
                                <TextInput
                                    id="filing-segurado-cpf"
                                    value={formatCpf(seguradoCpf)}
                                    onChange={(value) => {
                                        setSeguradoCpf(cpfDigits(value));
                                        setCpfError(null);
                                    }}
                                    invalid={!!cpfError}
                                    inputMode="numeric"
                                    placeholder="000.000.000-00"
                                    autoComplete="off"
                                />
                            </Field>
                            <Field label="Senha gov.br do segurado" htmlFor="filing-govbr-password" required>
                                <div className="relative">
                                    <TextInput
                                        id="filing-govbr-password"
                                        type={showPassword ? 'text' : 'password'}
                                        autoComplete="new-password"
                                        value={govbrPassword}
                                        onChange={setGovbrPassword}
                                        className="pr-10"
                                    />
                                    <button
                                        type="button"
                                        aria-label={showPassword ? 'Esconder senha' : 'Mostrar senha'}
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-[#8a8a94] hover:text-[#1d1d1f]"
                                    >
                                        {showPassword ? (
                                            <EyeOff className="h-4 w-4" aria-hidden="true" />
                                        ) : (
                                            <Eye className="h-4 w-4" aria-hidden="true" />
                                        )}
                                    </button>
                                </div>
                            </Field>
                        </div>
                        <Field label="Serviço no INSS" htmlFor="filing-inss-service">
                            <SelectInput
                                id="filing-inss-service"
                                value={inssService}
                                onChange={setInssService}
                                options={INSS_SERVICES}
                            />
                        </Field>
                        {procurador.procuradorName && (
                            <p className="m-0 text-xs text-[#6b6b76]" data-testid="filing-procurador">
                                {`Procurador: ${titleCaseName(procurador.procuradorName)}${
                                    procurador.procuradorCpfMasked ? ` (CPF ${procurador.procuradorCpfMasked})` : ''
                                }`}
                            </p>
                        )}
                        {procuradorBlocked && (
                            <Notice
                                tone="warning"
                                icon={AlertTriangle}
                                action={
                                    <Link
                                        to="/profile#filing"
                                        className="text-sm font-medium text-primary hover:underline"
                                    >
                                        Completar
                                    </Link>
                                }
                            >
                                Complete o CPF do advogado na sua conta: ele entra como procurador no Meu INSS.
                            </Notice>
                        )}
                        <p className="m-0 text-xs text-[#8a8a94]">
                            A senha é usada só neste envio e não fica guardada.
                        </p>
                    </Block>
                ) : (
                    <Block
                        title="Quem assina"
                        aside={
                            chosenSigners.length > 1 ? (
                                <span className="text-xs text-[#6b6b76]">{`${chosenSigners.length} advogados assinam`}</span>
                            ) : null
                        }
                    >
                        {signers.length > 1 && hasReadySigner && (
                            <p className="m-0 flex items-center gap-1.5 text-xs text-[#6b6b76]">
                                <Users className="h-3.5 w-3.5" aria-hidden="true" />
                                Pode marcar mais de um. O principal entra no tribunal e assina primeiro.
                            </p>
                        )}
                        {signers.length ? (
                            signers.map((signer) => {
                                const canConnect =
                                    !signer.ready &&
                                    CONNECTABLE.includes(signer.reasonCode) &&
                                    !!signerGroup;
                                // Posição entre os marcados que estão prontos: o principal é sempre o 1º deles.
                                const position = chosenSigners.findIndex(
                                    (chosen) =>
                                        Number(chosen.certificateId) === Number(signer.certificateId)
                                );

                                return (
                                    <SignerRow
                                        key={signer.certificateId}
                                        signer={signer}
                                        position={position}
                                        selected={position >= 0}
                                        isPrincipal={position === 0 && chosenSigners.length > 1}
                                        ordered={position >= 0 && chosenSigners.length > 1}
                                        courtPending={courtPending}
                                        canConnect={canConnect}
                                        connectLabel={
                                            signer.reasonCode === 'CODIGO_EM_ATIVACAO' ? 'Ver ativação'
                                                : signer.reasonCode === 'CODIGO_ATIVACAO_FALHOU' ? failureCopyOf({ autoActivation: true, failure: signer.failure }).label || 'Ver detalhes'
                                                : signer.reasonCode === 'TRIBUNAL_RECONECTAR'
                                                ? 'Reconectar'
                                                : groupNeedsTotp
                                                ? 'Conectar'
                                                : 'Adicionar'
                                        }
                                        connecting={connectLoading === signer.certificateId}
                                        onToggle={() => toggleSigner(signer.certificateId)}
                                        onPrincipal={() => makePrincipal(signer.certificateId)}
                                        onConnect={() => openConnect(signer)}
                                    />
                                );
                            })
                        ) : (
                            <Notice
                                tone="warning"
                                icon={AlertTriangle}
                                action={
                                    <Link
                                        to="/profile#filing"
                                        className="text-sm font-medium text-primary hover:underline"
                                    >
                                        Configurar
                                    </Link>
                                }
                            >
                                {`Envie o certificado A1 de quem vai assinar e conecte o ${courtShort || 'tribunal'}.`}
                                {groupNeedsTotp && (
                                    <span className="mt-1 block" data-testid="filing-totp-hint">
                                        {`Conecte o código de acesso de quem assina. Nos tribunais com ativação automática, a Facilita faz o cadastro para você.`}
                                    </span>
                                )}
                            </Notice>
                        )}
                        {notEnabledChosen.length > 0 && (
                            <Notice tone="warning" icon={AlertTriangle}>
                                {`${notEnabledChosen
                                    .map((signer) => titleCaseName(signer.name))
                                    .join(', ')} não ${
                                    notEnabledChosen.length > 1 ? 'aparecem' : 'aparece'
                                } como advogado habilitado neste processo. O tribunal recusaria a petição: desmarque ou escolha outro advogado.`}
                            </Notice>
                        )}
                        {connectTarget && (
                            <AccessSetupModal
                                key={`${connectTarget.certificate.id}-${connectTarget.access.key}`}
                                open
                                access={connectTarget.access}
                                certificate={connectTarget.certificate}
                                onClose={() => setConnectTarget(null)}
                                onActivated={async () => {
                                    const target = { certificateId: connectTarget.certificate.id, accessGroup: connectTarget.access.key };
                                    setPendingActivations(current => [...current.filter(item => item.certificateId !== target.certificateId || item.accessGroup !== target.accessGroup), target]);
                                    await onReload();
                                }}
                                onConnected={async () => {
                                    await onReload();
                                    setSelectedIds((current) =>
                                        current.includes(connectTarget.certificate.id)
                                            ? current
                                            : [...current, connectTarget.certificate.id]
                                    );
                                }}
                            />
                        )}
                    </Block>
                )}

                {requirements.length > 0 && (
                    <Block title={meuInss ? 'Documentos que o INSS pede' : 'Documentos que o tribunal pede'}>
                        {requirements
                            .filter((item) => item.required && !item.alternativeGroup)
                            .map((item) => (
                                <RequirementRow
                                    key={item.key}
                                    item={item}
                                    requestId={requestId}
                                    uploading={uploadingMissing}
                                    onPick={pickMissing}
                                    availableDocuments={canSelectRequirements ? availableDocuments : []}
                                    onSelect={selectRequirement}
                                    waived={isWaived(item.key)}
                                    onWaive={(target) => toggleWaived(target.key)}
                                />
                            ))}
                        {groups.map(
                            ({
                                group,
                                items,
                                alternatives,
                                chosen,
                                chosenItems,
                                chosenHasFile
                            }) => (
                                <div
                                    key={group}
                                    className="flex flex-col gap-2 rounded-lg border border-[#ececf0] p-3"
                                >
                                    <p className="m-0 text-sm font-medium">
                                        {GROUP_TITLES[group] || 'Escolha uma opção'}
                                    </p>
                                    {alternatives.length > 1 && (
                                        <div
                                            className="grid grid-cols-1 gap-2 sm:grid-cols-2"
                                            role="radiogroup"
                                            aria-label={GROUP_TITLES[group] || 'Escolha uma opção'}
                                        >
                                            {alternatives.map((alternative) => (
                                                <Choice
                                                    key={alternative}
                                                    name={`filing-group-${group}`}
                                                    checked={chosen === alternative}
                                                    onSelect={() =>
                                                        setGroupChoices((current) => ({
                                                            ...current,
                                                            [group]: alternative
                                                        }))
                                                    }
                                                >
                                                    <span className="text-sm">
                                                        {ALTERNATIVE_LABELS[alternative] ||
                                                            items.find(
                                                                (item) =>
                                                                    item.alternative === alternative
                                                            ).label}
                                                    </span>
                                                </Choice>
                                            ))}
                                        </div>
                                    )}
                                    {chosenItems.length > 1 && (
                                        <p className="m-0 text-xs text-[#6b6b76]">
                                            Basta um dos arquivos abaixo.
                                        </p>
                                    )}
                                    {chosenItems.map((item) => (
                                        <RequirementRow
                                            key={item.key}
                                            item={item}
                                            requestId={requestId}
                                            uploading={uploadingMissing}
                                            onPick={pickMissing}
                                            availableDocuments={canSelectRequirements ? availableDocuments : []}
                                            onSelect={selectRequirement}
                                            covered={chosenHasFile || isWaived(group)}
                                            waived={isWaived(group)}
                                            onWaive={items.every((entry) => entry.waivable || !entry.required)
                                                ? () => toggleWaived(group) : undefined}
                                        />
                                    ))}
                                    {items.some(
                                        (item) =>
                                            item.alternative === chosen && item.askFeeSlipNumber
                                    ) && (
                                        <label
                                            className="flex flex-col gap-1 text-xs text-[#6b6b76]"
                                            htmlFor="filing-fee-slip-number"
                                        >
                                            Número da guia (opcional)
                                            <TextInput
                                                id="filing-fee-slip-number"
                                                value={feeSlipNumber}
                                                onChange={setFeeSlipNumber}
                                                inputMode="numeric"
                                                placeholder="Só os números"
                                                autoComplete="off"
                                            />
                                        </label>
                                    )}
                                </div>
                            )
                        )}
                        {requirements
                            .filter((item) => !item.required && !item.alternativeGroup)
                            .map((item) => (
                                <RequirementRow
                                    key={item.key}
                                    item={item}
                                    requestId={requestId}
                                    uploading={uploadingMissing}
                                    onPick={pickMissing}
                                    availableDocuments={canSelectRequirements ? availableDocuments : []}
                                    onSelect={selectRequirement}
                                />
                            ))}
                    </Block>
                )}

                {hasDocumentList ? (
                    <Block
                        title={meuInss ? 'O que vai para o INSS' : 'O que vai para o tribunal'}
                        aside={
                            <span className="text-xs text-[#6b6b76]">
                                {`${attachments.length + 1} ${
                                    attachments.length ? 'documentos' : 'documento'
                                } · `}
                                {pages.unknown
                                    ? `${pages.total ? `${pages.total}+ páginas` : 'páginas'} (${
                                          pages.unknown
                                      } sem contagem)`
                                    : `${pages.total} ${
                                          pages.total === 1 ? 'página' : 'páginas'
                                      } no total`}
                            </span>
                        }
                    >
                        <ol
                            className="m-0 flex list-none flex-col gap-2 p-0"
                            aria-label="Ordem final dos documentos"
                        >
                            <li className="flex items-center gap-3 rounded-lg border border-[color-mix(in_srgb,var(--color-primary)_30%,#fff)] bg-primaryTint px-3 py-2.5">
                                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">
                                    1
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-sm font-medium">
                                        {piece?.name ||
                                            (documentChoice === 'final'
                                                ? 'Sua versão final (escolha o arquivo)'
                                                : 'Peça entregue')}
                                    </span>
                                    <span className="block text-xs text-[#6b6b76]">
                                        {[
                                            'Peça principal',
                                            Number(piece?.pages) > 0 ? `${piece.pages} pág.` : null
                                        ]
                                            .filter(Boolean)
                                            .join(' · ')}
                                    </span>
                                </span>
                                {piece?.url && (
                                    <a href={piece.url} {...(documentChoice === 'final' ? finalLinkProps : { target: '_blank' })} rel="noreferrer"
                                        className="text-xs font-medium text-primary hover:underline">Abrir</a>
                                )}
                            </li>
                            {attachments.map((item, index) => {
                                const document = documentsById.get(Number(item.id));
                                if (!document) return null;
                                return (
                                    <AttachmentRow
                                        key={item.id}
                                        index={index}
                                        count={attachments.length}
                                        document={document}
                                        item={item}
                                        secretReason={secretReasonOf(item.id, caseData)}
                                        onMove={(offset) =>
                                            setAttachments((current) =>
                                                moveItem(current, index, offset)
                                            )
                                        }
                                        onRemove={() => removeAttachment(item.id)}
                                        onSecret={(secret) =>
                                            setAttachments((current) =>
                                                current.map((entry) =>
                                                    entry.id === item.id
                                                        ? { ...entry, secret }
                                                        : entry
                                                )
                                            )
                                        }
                                    />
                                );
                            })}
                        </ol>
                        {!attachments.length && (
                            <p className="m-0 text-xs text-[#6b6b76]">Sem anexos: vai só a peça.</p>
                        )}
                        {removedDocuments.length > 0 && (
                            <div className="flex flex-wrap items-center gap-2 text-xs text-[#6b6b76]">
                                <span>Fora do envio:</span>
                                {removedDocuments.map((document) => (
                                    <button
                                        key={document.id}
                                        type="button"
                                        onClick={() => restoreAttachment(document.id)}
                                        className="inline-flex items-center gap-1 rounded-full border border-[#dcdce2] bg-white px-2 py-0.5 hover:border-primary hover:text-primary"
                                        aria-label={`${
                                            removedSet.has(Number(document.id))
                                                ? 'Devolver'
                                                : 'Incluir'
                                        } ${document.name}`}
                                    >
                                        <RotateCcw className="h-3 w-3" aria-hidden="true" />
                                        {document.name}
                                    </button>
                                ))}
                            </div>
                        )}
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <ActionButton
                                size="sm"
                                variant="secondary"
                                icon={Plus}
                                loading={uploadingMissing === 'extra'}
                                disabled={!!uploadingMissing && uploadingMissing !== 'extra'}
                                onClick={() => pick({ key: 'extra' })}
                            >
                                Anexar documento
                            </ActionButton>
                            <p className="m-0 text-xs text-[#8a8a94]">
                                Documentos internos do escritório não vão ao tribunal.
                            </p>
                        </div>
                    </Block>
                ) : (
                    !requirements.length && (
                        <p className="m-0 flex items-center gap-2 text-sm text-[#1d1d1f]">
                            <CheckCircle2 className="h-4 w-4 text-[#1f7a4f]" aria-hidden="true" />
                            Organizamos e nomeamos os anexos no padrão do{' '}
                            {panel.court?.name?.split(' · ')[0] || 'tribunal'}.
                        </p>
                    )
                )}
                {needsListAcknowledgement && (
                    <Block title="Sua lista de documentos">
                        <div className="flex flex-col gap-2 rounded-lg border border-[#f3d9b1] bg-[#fdf6ec] px-4 py-3 text-sm">
                            {listSummary.removed.length > 0 && (
                                <p className="m-0">
                                    <span className="font-medium">Tirados da lista: </span>
                                    {listSummary.removed.join(', ')}
                                </p>
                            )}
                            {listSummary.waived.length > 0 && (
                                <p className="m-0">
                                    <span className="font-medium">Exigidos que você vai dispensar: </span>
                                    {listSummary.waived.join(', ')}
                                </p>
                            )}
                            {listSummary.withoutFile.length > 0 && (
                                <p className="m-0">
                                    <span className="font-medium">Citados na peça, sem arquivo: </span>
                                    {listSummary.withoutFile.join(', ')}
                                </p>
                            )}
                            <label className="flex cursor-pointer items-start gap-2">
                                <input
                                    type="checkbox"
                                    className="form-checkbox mt-0.5 h-4 w-4 rounded border-[#c4c4cc] text-primary focus:ring-primary"
                                    checked={listAcknowledged}
                                    onChange={(event) => setAcknowledgedKey(event.target.checked ? listKey : null)}
                                />
                                <span>{LIST_ACKNOWLEDGEMENT_TEXT}</span>
                            </label>
                        </div>
                    </Block>
                )}
                <input
                    ref={attachRef}
                    type="file"
                    accept={ATTACH_ACCEPT}
                    className="hidden"
                    data-testid="filing-attach-input"
                    onChange={(event) => {
                        sendAttachment(attachRef.current?.target, event.target.files?.[0]);
                        event.target.value = '';
                    }}
                />

                <Block title="Taxa">
                    {/* Preço centralizado na altura do cartão, ao lado do nome e da explicação. */}
                    <div className="flex items-center justify-between gap-4 rounded-lg bg-[#fafafb] px-4 py-3 text-sm">
                        <div className="min-w-0">
                            <p className="m-0">
                                {verbs.action === 'Distribuir' ? 'Distribuição' : 'Protocolo'} pela
                                Facilita
                            </p>
                            <p className="m-0 mt-0.5 text-xs text-[#6b6b76]">
                                {fee > 0
                                    ? `${formatMoney(fee)} por ${
                                          verbs.noun
                                      }, debitado do seu pacote. Só cobramos quando você confirma.`
                                    : 'Seu plano inclui protocolo e distribuição sem custo.'}
                            </p>
                        </div>
                        <span className="shrink-0 font-semibold">
                            {fee > 0 ? formatMoney(fee) : 'Grátis no seu plano'}
                        </span>
                    </div>
                </Block>

                {error && (
                    <Notice
                        tone="danger"
                        action={
                            blankFieldsRefused ? (
                                <ActionButton
                                    size="sm"
                                    variant="secondary"
                                    loading={isSending}
                                    onClick={() => submit({ confirmBlankFields: true })}
                                >
                                    Está completa, {verbs.action.toLowerCase()} assim
                                </ActionButton>
                            ) : undefined
                        }
                    >
                        {error}
                    </Notice>
                )}
            </div>
        </Modal>
    );
}
